import axios from 'axios'
import * as tokenService from '@/auth/tokenService'
import baseURLs from '@/api/APIBaseURLs'

// `@/router` and `@/store/pinia/generalStore` are imported LAZILY (dynamic import
// inside the runtime-only error handlers) on purpose. Importing them statically
// pulls the whole route/view graph and the store into this module's init, creating
// import cycles — e.g. generalStore -> FESettings -> @/services/axios -> router ->
// views -> StoresSelect -> generalStore (TDZ on useGeneralStore). Keeping this
// module light at init avoids that entire class of cycles.

const service = axios.create({
  baseURL: '',
  headers: tokenService.getHeadersWithAccessToken()
})

const AUTH_ROUTES = ['/login', '/reset_password', '/forgot-password']

async function redirectToLoginIfNeeded () {
  const router = (await import('@/router')).default
  const path = router.currentRoute.value?.path || ''
  if (!AUTH_ROUTES.includes(path)) {
    try {
      await router.push({ path: '/login', query: { redirect: path } })
    } catch (error) {
      console.warn('login redirect failed', error)
    }
  }
}

async function withGeneralStore (mutate) {
  const { useGeneralStore } = await import('@/store/pinia/generalStore')
  mutate(useGeneralStore())
}

/** Requests we must never attach/refresh the main JWT for (separate token / no auth). */
const isStorepageUrl = (url = '') => !!baseURLs.storepageAPI && url.includes(baseURLs.storepageAPI)

function applyAuthHeader (config, token) {
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`)
  } else {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` }
  }
}

/**
 * Proactive best-effort request interceptor: when the access token is missing/expiring and a
 * refresh token exists, refresh BEFORE firing the request. Concurrent requests
 * share a single refresh via tokenService's dedupe. Storepage requests are skipped
 * (they use a separate token).
 */
export async function onRequest (config) {
  if (isStorepageUrl(config.url)) {
    return config
  }
  const result = await tokenService.ensureValidAccessToken()
  if (result.ok && result.accessToken) {
    applyAuthHeader(config, result.accessToken)
  }
  return config
}

/**
 * Response error interceptor:
 *  - 401/422 (non-storepage, not already retried): reactive refresh + replay once.
 *    tokenService.refresh() clears tokens on an auth failure, so a failed refresh
 *    means the session is dead → redirect to login.
 *  - 400/500: surface a warning snackbar.
 *  - 403: show the access-denied dialog.
 */
export async function onResponseError (error) {
  const originalRequest = error.config || {}
  const status = error.response?.status
  const isStorepage = isStorepageUrl(originalRequest.url)

  if (
    (status === 401 || status === 422) &&
    !originalRequest._retry &&
    !isStorepage
  ) {
    originalRequest._retry = true
    const result = await tokenService.refresh()
    if (!result.ok || !result.accessToken) {
      console.error('refresh failed', result)
      await redirectToLoginIfNeeded()
      return Promise.reject(error)
    }
    originalRequest.headers = tokenService.getHeadersWithAccessToken(result.accessToken)
    return service.request(originalRequest) // retry through the interceptor chain
  }

  if (status === 400 || status === 500) {
    const responseURL = error.request?.responseURL || originalRequest.url || ''
    const parts = responseURL.split('/')
    const endpoint = '/' + parts[parts.length - 1]
    const method = error.response?.config?.method || originalRequest.method || ''
    const message = method + '' + endpoint + '<br>' + JSON.stringify(error.response?.data || '')
    await withGeneralStore((store) => store.setSnack('warning', message))
  }

  if (status === 403) {
    const responseURL = error.request?.responseURL || originalRequest.url || ''
    const parts = responseURL.split('/')
    const endpoint = '/' + parts[parts.length - 1]
    await withGeneralStore((store) => {
      store.setAccessDeniedDialogVisible(true)
      store.setAccessDeniedDialogContent(endpoint)
    })
  }

  return Promise.reject(error)
}

service.interceptors.request.use(onRequest, (error) => Promise.reject(error))
service.interceptors.response.use((response) => response, onResponseError)

export const setAxiosAccessToken = (token) => {
  service.defaults.headers.common['Authorization'] = `Bearer ${token}`
  service.defaults.headers['Authorization'] = `Bearer ${token}`
}

export const revokeAxiosAccessToken = () => {
  delete service.defaults.headers.common['Authorization']
  delete service.defaults.headers['Authorization']
}

// tokenService owns token state but must not import this module (cycle). It pushes
// token changes here instead, keeping the instance's default header in sync.
tokenService.setTokenSink((token) => {
  if (token) {
    setAxiosAccessToken(token)
  } else {
    revokeAxiosAccessToken()
  }
})

export default service
